<template>
  <b-modal
    :visible="visible"
    title="Assistente de Otimização"
    size="lg"
    hide-footer
    no-close-on-backdrop
    @hide="onHide"
  >
    <!-- Step indicator -->
    <div class="ram-steps">
      <div v-for="(label, idx) in stepLabels" :key="idx" class="ram-step-item">
        <div :class="['ram-step-badge', { 'ram-step-badge--active': currentStep >= idx, 'ram-step-badge--done': currentStep > idx }]">
          <b-icon v-if="currentStep > idx" icon="check" />
          <span v-else>{{ idx + 1 }}</span>
        </div>
        <span :class="['ram-step-label', { 'ram-step-label--active': currentStep >= idx }]">{{ label }}</span>
        <div v-if="idx < stepLabels.length - 1" class="ram-step-line" />
      </div>
    </div>

    <hr class="ram-divider" />

    <!-- Step 0: Seleção -->
    <div v-if="currentStep === 0" class="ram-content">
      <div v-if="loadingConfig" class="ram-loading-config">
        <b-spinner small variant="secondary" />
        <span>Carregando objetivos...</span>
      </div>
      <div v-else-if="!objectives.length" class="ram-empty">
        Nenhum objetivo configurado. Abra as Configurações RTO e adicione objetivos.
      </div>
      <div v-else>
        <p class="ram-section-label">Selecione o objetivo de otimização:</p>
        <div
          v-for="obj in objectives"
          :key="obj.id"
          :class="['ram-obj-row', { 'ram-obj-row--selected': selectedObjectiveId === obj.id }]"
          @click="selectedObjectiveId = obj.id"
        >
          <input type="radio" :checked="selectedObjectiveId === obj.id" class="ram-radio" @change="selectedObjectiveId = obj.id" />
          <div class="ram-obj-info">
            <span class="ram-obj-label">{{ obj.label }}</span>
            <span v-if="!obj.corrente || !obj.propriedade || !obj.atributo" class="ram-obj-warn">
              <b-icon icon="exclamation-triangle" /> Variável objetivo não configurada — edite no drawer
            </span>
            <span v-else-if="!(obj.areas || []).some(a => (a.variables || []).some(v => v.mapping))" class="ram-obj-warn">
              <b-icon icon="exclamation-triangle" /> Nenhuma variável manipulada — edite no drawer
            </span>
          </div>
        </div>
      </div>

      <div v-if="preparingSnapshot" class="ram-preparing">
        <b-spinner small variant="warning" />
        <span>Atualizando condições operacionais com o estado atual do PAP…</span>
      </div>

      <div class="ram-footer">
        <b-button variant="secondary" size="sm" @click="close" :disabled="preparingSnapshot">Cancelar</b-button>
        <b-button
          class="orange-btn"
          size="sm"
          :disabled="!canStart || preparingSnapshot"
          @click="startOptimization"
        >
          <b-spinner v-if="preparingSnapshot" small />
          <span v-if="preparingSnapshot">Atualizando…</span>
          <span v-else>Iniciar Otimização</span>
        </b-button>
      </div>
    </div>

    <!-- Step 1: Execução -->
    <div v-if="currentStep === 1" class="ram-content ram-content--execution">
      <div class="ram-execution-body">
        <b-spinner variant="warning" style="width: 2.5rem; height: 2.5rem;" />
        <p class="ram-execution-status">{{ executionStatusLabel }}</p>
        <p v-if="errorMessage" class="ram-execution-error">{{ errorMessage }}</p>
      </div>
      <div class="ram-footer">
        <b-button v-if="processamentoId" variant="outline-info" size="sm" @click="openSeqMonitor">
          <b-icon icon="broadcast" class="mr-1" /> Acompanhar em tempo real
        </b-button>
        <b-button variant="outline-danger" size="sm" :disabled="isCancelling" @click="cancelOptimization">
          <b-spinner v-if="isCancelling" small />
          {{ isCancelling ? 'Cancelando...' : 'Cancelar Otimização' }}
        </b-button>
      </div>
    </div>

    <!-- Step 2: Resultados -->
    <div v-if="currentStep === 2" class="ram-content">
      <!-- Erro -->
      <div v-if="executionStatus === 'ErroIdentificadoSegundoRetorno'" class="ram-result-error">
        <b-icon icon="x-circle-fill" class="ram-result-error-icon" />
        <p>A otimização encontrou um erro.</p>
        <p v-if="errorMessage" class="ram-execution-error">{{ errorMessage }}</p>
      </div>
      <!-- Cancelado -->
      <div v-else-if="executionStatus === 'Cancelada'" class="ram-result-cancelled">
        <b-icon icon="slash-circle" class="ram-result-cancelled-icon" />
        <p>Otimização cancelada. Resultado parcial exibido abaixo (melhor ponto encontrado até o cancelamento).</p>
      </div>

      <!-- Variável objetivo: antes x depois da otimização -->
      <div v-if="objectiveSummary" class="ram-obj-summary">
        <div class="ram-obj-summary-head">
          <b-icon icon="bullseye" class="mr-1" />
          Variável Objetivo
          <span class="ram-obj-summary-tag">{{ objectiveSummary.tag }}</span>
        </div>
        <div class="ram-obj-summary-body">
          <div class="ram-obj-summary-cell">
            <label>Antes</label>
            <span class="ram-obj-summary-val">{{ objectiveSummary.before === 0 ? '-' : formatNum(objectiveSummary.before) }}</span>
          </div>
          <b-icon icon="arrow-right" class="ram-obj-summary-arrow" />
          <div class="ram-obj-summary-cell">
            <label>Depois</label>
            <span class="ram-obj-summary-val ram-obj-summary-val--after">{{ formatNum(objectiveSummary.after) }}</span>
          </div>
          <span :class="['ram-obj-summary-var', variationClass(objectiveSummary.variation)]">
            {{ formatVariation(objectiveSummary.variation) }}
          </span>
        </div>
      </div>

      <!-- Tabela de resultados -->
      <div v-if="resultRows.length" class="ram-result-table-wrap">
        <table class="ram-result-table">
          <thead>
            <tr>
              <th>Parâmetro</th>
              <th>Valor Atual</th>
              <th>Otimizado</th>
              <th>Variação</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in resultRows" :key="row.tag">
              <td class="ram-td-label">{{ row.label }}</td>
              <td class="ram-td-num">{{ formatNum(row.current) }}</td>
              <td class="ram-td-num">{{ formatNum(row.optimized) }}</td>
              <td :class="['ram-td-num', variationClass(row.variation)]">
                {{ formatVariation(row.variation) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else-if="executionStatus === 'Finalizado'" class="ram-empty">
        Otimização concluída sem variáveis manipuladas mapeadas no resultado.
      </div>

      <p v-if="errorMessage" class="ram-execution-error">{{ errorMessage }}</p>

      <div class="ram-footer">
        <b-button variant="secondary" size="sm" @click="close">Rejeitar</b-button>
        <b-button
          class="orange-btn"
          size="sm"
          :disabled="!canApply"
          @click="applyOptimization"
        >
          Aprovar e Aplicar
        </b-button>
      </div>
    </div>

  </b-modal>
</template>

<script>
import axios from 'axios';
import { urlMainAPI } from '@/global';
import { parseBR } from '../flowchart/utils/numbers';
import { SingletonEnginnering } from '../flowchart/nodes/_service/singletonEngineering';
import { SingletonFlowchart } from '../flowchart/nodes/_service/singletonFlowchart';
import { SingletonAreas } from '../flowchart/nodes/_service/singletonAreas';
import { isRtoUnreachableError, RTO_UNREACHABLE_MESSAGE } from './rtoErrors';
import { fromRtoScale } from './rtoScale';
import { safeRandomUUID } from '@/services/uuid';
export default {
  name: 'RtoAssistentModal',

  props: {
    visible: { type: Boolean, default: false },
  },

  emits: ['close', 'apply', 'open-seq', 'seq-iteracao'],

  data() {
    return {
      currentStep: 0,
      stepLabels: ['Seleção', 'Execução', 'Resultados'],
      loadingConfig: false,
      objectives: [],
      selectedObjectiveId: null,
      processamentoId: null,
      protocolId: null,
      pollingInterval: null,
      executionStatus: '',
      executionResult: null,
      isCancelling: false,
      errorMessage: '',
      condicoesOperacionais: [],
      preparingSnapshot: false,
    };
  },

  computed: {
    selectedObjectiveLabel() {
      const obj = this.objectives.find(o => o.id === this.selectedObjectiveId);
      return obj?.label ?? '';
    },

    canStart() {
      const obj = this.objectives.find(o => o.id === this.selectedObjectiveId);
      if (!obj) return false;
      if (!obj.rtoOtimizacaoId) return false;
      if (!obj.corrente || !obj.propriedade || !obj.atributo) return false;
      return (obj.areas || []).some(a => (a.variables || []).some(v => v.mapping));
    },

    canApply() {
      return this.executionStatus === 'Finalizado' && this.resultRows.length > 0;
    },

    executionStatusLabel() {
      const map = {
        '': 'Enviando requisição...',
        NaoIniciado: 'Aguardando o otimizador iniciar...',
        Pendente: 'Enfileirando...',
        PendenteRetornoOtimizador: 'Otimizando — aguarde...',
        EmLegitimacao: 'Validando resultado...',
        EmProcessamento: 'Otimizando — aguarde...',
      };
      return map[this.executionStatus] ?? `Status: ${this.executionStatus}`;
    },

    resultRows() {
      if (!this.executionResult) return [];
      // RTOAPI retorna inputMapping: [{tag, value}] com os valores otimizados das manipuladas
      const inputMapping = this.executionResult.inputMapping ?? this.executionResult.InputMapping ?? [];
      return inputMapping.map(m => {
        const tag = m.tag ?? m.Tag ?? '';
        // O RTO devolve o valor otimizado em escala interna (fração/Kelvin).
        // fromRtoScale converte pra escala PAP (%/Celsius) — mesma de "current" —
        // pra a variação fazer sentido e o applyOptimization gravar na escala certa.
        const optimizedRaw = m.value ?? m.Value ?? null;
        const optimized = optimizedRaw != null ? fromRtoScale(optimizedRaw, tag) : null;
        const raw = SingletonEnginnering.getValueByToken(tag);
        const current = raw != null ? parseBR(raw) : null;
        const variation = current != null && !isNaN(current) && optimized != null && current !== 0
          ? ((optimized - current) / Math.abs(current)) * 100
          : null;
        return { tag, label: tag, current, optimized, variation };
      });
    },

    // Variável objetivo: valor antes (singleton/PAP) x depois (resultado do RTO).
    // funcaoObjetivo.valorSimulado vem em escala RTO — fromRtoScale alinha com o "antes".
    objectiveSummary() {
      const fo = this.executionResult && this.executionResult.funcaoObjetivo;
      if (!fo || !fo.tag) return null;
      const after = fo.valorSimulado != null ? fromRtoScale(fo.valorSimulado, fo.tag) : null;
      const rawBefore = SingletonEnginnering.getValueByToken(fo.tag);
      const before = rawBefore != null ? parseBR(rawBefore) : null;
      const variation = before != null && !isNaN(before) && after != null && before !== 0
        ? ((after - before) / Math.abs(before)) * 100
        : null;
      return { tag: fo.tag, before, after, variation };
    },
  },

  watch: {
    visible(val) {
      if (val) {
        this.reset();
        this.loadConfig();
      } else {
        this.cleanup();
      }
    },
  },

  methods: {
    reset() {
      this.currentStep = 0;
      this.selectedObjectiveId = null;
      this.processamentoId = null;
      this.protocolId = null;
      this.executionStatus = '';
      this.executionResult = null;
      this.isCancelling = false;
      this.errorMessage = '';
    },

    cleanup() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },

    openSeqMonitor() {
      this.$emit('open-seq', {
        processamentoId: this.processamentoId,
        otimizacaoNome: this.selectedObjectiveLabel,
        tagToLabel: this.buildTagToLabel(),
      });
    },

    // Mapa tag→apelido do objetivo selecionado, para o monitor exibir o apelido em vez da tag crua.
    buildTagToLabel() {
      const map = {};
      const obj = this.objectives.find(o => o.id === this.selectedObjectiveId);
      if (!obj) return map;
      if (obj.objectiveTag && obj.label) map[obj.objectiveTag] = obj.label;
      for (const area of (obj.areas || [])) {
        for (const v of (area.variables || [])) {
          if (v.mapping && v.label) map[v.mapping] = v.label;
        }
        for (const r of (area.restrictions || [])) {
          if (r.mapping && r.label) map[r.mapping] = r.label;
        }
      }
      return map;
    },

    parseTag(tag) {
      if (!tag || typeof tag !== 'string') return null;
      const parts = tag.split('|');
      const type = parts[0];
      if ((type === 'cr' || type === 'cp') && parts.length === 4) {
        return { corrente: parts[1], propriedade: { name: parts[2] }, atributo: parts[3] };
      }
      return null;
    },

    buildObjectiveFromRto(otm) {
      const elements = Array.isArray(otm.elementos) ? otm.elementos : [];
      const objElem = elements.find(e => e.tipo === 'Objetivo' || e.tipo === 0);
      const parsedObj = objElem ? this.parseTag(objElem.tag) : null;

      const mode = (objElem?.objetivoTipo === 'Fixed' || objElem?.objetivoTipo === 2) ? 'fixed' : 'optimize';
      const optimizeType = (objElem?.objetivoTipo === 'Min' || objElem?.objetivoTipo === 0) ? 'min' : 'max';

      const variables = [];
      const restrictions = [];
      for (const el of elements) {
        if (el.tipo === 'Objetivo' || el.tipo === 0) continue;
        const parsed = this.parseTag(el.tag);
        const base = {
          id: safeRandomUUID(),
          corrente: parsed?.corrente ?? null,
          propriedade: parsed?.propriedade ?? null,
          atributo: parsed?.atributo ?? null,
          label: el.label || parsed?.corrente || el.tag,
          min: el.min ?? 0,
          max: el.max ?? 100,
          mapping: el.tag,
        };
        if (el.tipo === 'Manipulada' || el.tipo === 1) {
          variables.push(base);
        } else if (el.tipo === 'Restricao' || el.tipo === 2) {
          restrictions.push({ ...base, enabled: true });
        }
      }

      return {
        id: String(otm.id),
        label: otm.nome,
        rtoOtimizacaoId: String(otm.id),
        objectiveTag: objElem?.tag ?? null,
        mode,
        optimizeType,
        fixedValue: objElem?.fixedValue ?? 0,
        corrente: parsedObj?.corrente ?? null,
        propriedade: parsedObj?.propriedade ?? null,
        atributo: parsedObj?.atributo ?? null,
        areas: [{
          id: safeRandomUUID(),
          label: 'Padrão',
          variables,
          restrictions,
        }],
      };
    },

    async loadConfig() {
      this.loadingConfig = true;
      try {
        const res = await axios.get(`${urlMainAPI}/configurator/rto/listar`);
        const data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        const list = (Array.isArray(data?.data) ? data.data : [])
          .filter(o => o.status !== 'Deletado');
        this.objectives = list.map(otm => this.buildObjectiveFromRto(otm));
        if (this.objectives.length === 1) {
          this.selectedObjectiveId = this.objectives[0].id;
        }
      } catch (e) {
        console.error('Erro ao carregar otimizações do RTO:', e);
      } finally {
        this.loadingConfig = false;
      }
    },

    buildHistorianEntries(objective) {
      const entries = [];
      for (const area of (objective.areas || [])) {
        for (const variable of (area.variables || [])) {
          if (variable.mapping) {
            const raw = SingletonEnginnering.getValueByToken(variable.mapping);
            const parsed = raw != null ? parseBR(raw) : 0;
            const valor = Number.isFinite(parsed) ? parsed : 0;
            entries.push({ tag: variable.mapping, valor: this.toRtoSnapshotScale(variable.mapping, valor) });
          }
        }
      }
      for (const co of (this.condicoesOperacionais || [])) {
        if (!co?.tag) continue;
        if (entries.some(e => e.tag === co.tag)) continue;
        const raw = SingletonEnginnering.getValueByToken(co.tag);
        if (raw == null) continue;
        const parsed = parseBR(raw);
        if (!Number.isFinite(parsed)) continue;
        entries.push({ tag: co.tag, valor: this.toRtoSnapshotScale(co.tag, parsed) });
      }
      return entries;
    },

    // Terra/1_1: o canvas guarda fracaoMassica como impureza/10 (5 kg/tc -> 0,5), mas o
    // SetXMLAttribute do RTO (espelho do SimulatorController do PGDI) espera a escala de
    // exibicao e aplica /1000 (PropertyBrix /100 + regra do P.O. /10). Sem o x10 a Terra
    // sai /10 menor (0,5/1000 = 0,0005 em vez de 0,005). Os demais atributos ja vem certos.
    toRtoSnapshotScale(mapping, valor) {
      const parts = (mapping || '').split('|');
      const isTerra1_1 = parts.length === 4 && parts[0] === 'cp'
        && parts[1] === '1_1' && parts[2] === 'Terra' && parts[3] === 'fracaoMassica';
      return isTerra1_1 ? valor * 10 : valor;
    },

    async fetchCondicoesOperacionais() {
      try {
        const res = await axios.get(`${urlMainAPI}/configurator/rto/condicoes-operacionais`);
        const data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;
        const lista = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
        this.condicoesOperacionais = lista.filter(c => c && c.tag);
      } catch (e) {
        console.warn('Não foi possível carregar Condições Operacionais do RTO — snapshot vai sem elas:', e);
        this.condicoesOperacionais = [];
      }
    },

    async startOptimization() {
      const obj = this.objectives.find(o => o.id === this.selectedObjectiveId);
      if (!obj) return;
      if (!obj.rtoOtimizacaoId) {
        this.errorMessage = 'Objetivo sem ID RTO — salve a configuração no drawer antes de executar.';
        this.executionStatus = 'ErroIdentificadoSegundoRetorno';
        this.currentStep = 2;
        return;
      }

      this.preparingSnapshot = true;
      try {
        await this.fetchCondicoesOperacionais();
      } finally {
        this.preparingSnapshot = false;
      }

      const entries = this.buildHistorianEntries(obj);
      this.currentStep = 1;
      this.executionStatus = '';
      try {
        const res = await axios.post(
          `${urlMainAPI}/configurator/rto/executar/${obj.rtoOtimizacaoId}`,
          { historianEntries: entries }
        );
        const raw = res.data;
        const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
        this.processamentoId = data?.data?.processamentoId ?? data?.processamentoId ?? null;
        this.protocolId = data?.data?.protocolId ?? data?.protocolId ?? null;
        this.executionStatus = data?.data?.status ?? data?.status ?? 'NaoIniciado';
        if (this.processamentoId) {
          this.startPolling();
        } else {
          this.errorMessage = `processamentoId não encontrado. Resposta: ${JSON.stringify(data).slice(0, 200)}`;
          this.executionStatus = 'ErroIdentificadoSegundoRetorno';
          this.currentStep = 2;
        }
      } catch (err) {
        if (isRtoUnreachableError(err)) {
          this.errorMessage = RTO_UNREACHABLE_MESSAGE;
        } else {
          const errData = err?.response?.data;
          this.errorMessage = errData?.description ?? errData?.message ?? (typeof errData === 'string' ? errData : null) ?? err.message ?? 'Erro ao iniciar otimização.';
        }
        this.executionStatus = 'ErroIdentificadoSegundoRetorno';
        this.currentStep = 2;
      }
    },

    startPolling() {
      const finalStatuses = ['Finalizado', 'ErroIdentificadoSegundoRetorno', 'Erro', 'Cancelada'];
      this.pollingInterval = setInterval(async () => {
        try {
          const res = await axios.get(`${urlMainAPI}/configurator/rto/status/${this.processamentoId}`);
          const data = res.data;
          const status = data?.status ?? this.executionStatus;
          this.executionStatus = status;
          if (finalStatuses.includes(status)) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
            // resultado normalizado pelo proxy: { status, inputMapping, optValue, descricaoErro, resultado }
            this.executionResult = data?.resultado ?? data;
            if (status !== 'Finalizado') {
              this.errorMessage = data?.descricaoErro ?? 'Erro no otimizador.';
            }
            this.isCancelling = false;
            this.currentStep = 2;
          }
        } catch {
          // mantém polling — erros transitórios não devem parar a espera
        }
      }, 4000);
    },

    async cancelOptimization() {
      const messageNode = this.$createElement('div', { class: 'ram-cancel-msg' }, [
        this.$createElement('p', { class: 'ram-cancel-msg__intro' },
          'Esta ação cancela a otimização que está em execução no RTO no momento.'
        ),
        this.$createElement('p', { class: 'ram-cancel-msg__warn' }, [
          this.$createElement('b', '⚠️ Atenção: '),
          'se você disparou várias otimizações, o cancelamento atinge a ',
          this.$createElement('b', 'primeira da fila'),
          ' (a única realmente rodando), que pode ',
          this.$createElement('b', 'não ser a deste Assistente'),
          '.',
        ]),
        this.$createElement('p', { class: 'ram-cancel-msg__hint' },
          'Deseja continuar?'
        ),
      ]);

      const ok = await this.$bvModal.msgBoxConfirm(messageNode, {
        title: 'Cancelar otimização em execução?',
        okTitle: 'Cancelar otimização',
        okVariant: 'danger',
        cancelTitle: 'Voltar',
        cancelVariant: 'outline-secondary',
        size: 'md',
        centered: true,
        bodyClass: 'ram-cancel-body',
      });
      if (!ok) return;

      this.isCancelling = true;
      try {
        await axios.post(`${urlMainAPI}/configurator/rto/cancelar-em-execucao`);
        // isCancelling permanece true; só é liberado quando o polling detectar
        // um status final (Cancelada / Finalizado / Erro) e mudar para Step 2.
      } catch (e) {
        console.error('Erro ao cancelar otimização:', e);
        if (isRtoUnreachableError(e)) {
          this.errorMessage = RTO_UNREACHABLE_MESSAGE;
        } else {
          const msg = e?.response?.data?.description ?? e?.response?.data?.message ?? e?.response?.data ?? e?.message ?? 'desconhecido';
          this.errorMessage = `Falha ao cancelar: ${typeof msg === 'string' ? msg : JSON.stringify(msg)}`;
        }
        this.isCancelling = false;
      }
    },

    async applyOptimization() {
      const rows = this.resultRows.filter(r => r.optimized != null);

      // Fase 1 — valida TODAS as tags antes de aplicar qualquer uma.
      // Se alguma não resolver, aborta tudo: evita deixar o PAP num estado híbrido
      // (parte otimizado, parte pré-otimização) que não corresponde a nenhum resultado real.
      const unresolved = rows
        .filter(r => {
          const prop = SingletonEnginnering.getPropertieByToken(r.tag);
          return !prop || prop === '-9999';
        })
        .map(r => r.tag);

      if (unresolved.length) {
        this.errorMessage = `Não foi possível aplicar: ${unresolved.length} variável(is) com mapeamento inválido `
          + `(${unresolved.join(', ')}). Nenhuma alteração foi aplicada ao PAP.`;
        return;
      }

      this.errorMessage = '';

      // Fase 2 — aplica todas (já validadas) no SingletonEnginnering.
      for (const row of rows) {
        SingletonEnginnering.setValueByToken(row.tag, row.optimized);
      }

      // Propaga valores otimizados do SingletonEnginnering para os nodes do flowchart.
      // Sem isso, os Input nodes mantêm o valor pré-otimização — a simulação e o save
      // de cenário usariam os antigos, ignorando o resultado do RTO.
      await SingletonFlowchart.Memory.reloadNodes(false);
      await SingletonAreas.UpdateAreas();

      const obj = this.objectives.find(o => o.id === this.selectedObjectiveId);
      this.$emit('apply', { objectiveLabel: obj?.label ?? '' });
      this.close();
    },

    close() {
      this.cleanup();
      this.$emit('close');
    },

    onHide() {
      this.close();
    },

    formatNum(val) {
      if (val == null || isNaN(Number(val))) return '—';
      return Number(val).toLocaleString('pt-BR', { maximumFractionDigits: 4 });
    },

    formatVariation(val) {
      if (val == null) return '—';
      return `${val >= 0 ? '+' : ''}${val.toFixed(2)}%`;
    },

    variationClass(val) {
      if (val == null) return '';
      return val > 0 ? 'ram-td-positive' : val < 0 ? 'ram-td-negative' : '';
    },
  },
};
</script>

<style scoped>
/* Step indicator */
.ram-steps {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  margin-bottom: 4px;
}

.ram-step-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ram-step-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background-color: #e5e7eb;
  color: #9ca3af;
  font-size: 0.72rem;
  font-weight: 700;
  flex-shrink: 0;
  transition: background-color 250ms, color 250ms;
}

.ram-step-badge--active {
  background-color: #ff5f00;
  color: #ffffff;
}

.ram-step-badge--done {
  background-color: #16a34a;
  color: #ffffff;
}

.ram-step-label {
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #9ca3af;
  white-space: nowrap;
}

.ram-step-label--active {
  color: #374151;
}

.ram-step-line {
  width: 32px;
  height: 2px;
  background-color: #e5e7eb;
  margin: 0 4px;
}

.ram-divider {
  margin: 8px 0 16px;
  border-color: #f3f4f6;
}

/* Content */
.ram-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 160px;
}

.ram-section-label {
  font-size: 0.78rem;
  color: #6b7280;
  margin-bottom: 6px;
}

/* Objective rows */
.ram-obj-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 150ms, border-color 150ms;
}

.ram-obj-row:hover {
  background-color: #f9fafb;
  border-color: #d1d5db;
}

.ram-obj-row--selected {
  border-color: #ff5f00;
  background-color: #fff7ed;
}

.ram-radio {
  margin-top: 2px;
  flex-shrink: 0;
  accent-color: #ff5f00;
}

.ram-obj-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ram-obj-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #374151;
}

.ram-obj-warn {
  font-size: 0.68rem;
  color: #b45309;
  display: flex;
  align-items: center;
  gap: 4px;
}

.ram-obj-id {
  font-size: 0.68rem;
  color: #9ca3af;
}

/* Execution */
.ram-content--execution {
  justify-content: space-between;
}

.ram-execution-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 24px 0 16px;
}

.ram-execution-status {
  font-size: 0.88rem;
  color: #374151;
  font-weight: 500;
  margin: 0;
}

.ram-preparing {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin-top: 8px;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 4px;
  font-size: 0.78rem;
  color: #92400e;
}

.ram-execution-error {
  font-size: 0.75rem;
  color: #dc2626;
  margin: 0;
  text-align: center;
}

/* Results */
.ram-result-error,
.ram-result-cancelled {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 0.8rem;
}

.ram-result-error {
  background-color: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
}

.ram-result-error-icon {
  font-size: 1.1rem;
  flex-shrink: 0;
}

.ram-result-cancelled {
  background-color: #fefce8;
  color: #92400e;
  border: 1px solid #fde68a;
  flex-wrap: wrap;
}

.ram-result-cancelled-icon {
  font-size: 1.1rem;
  flex-shrink: 0;
  color: #b45309;
}

.ram-result-table-wrap {
  overflow-x: auto;
}

.ram-result-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.78rem;
}

.ram-result-table th {
  background-color: #f9fafb;
  color: #6b7280;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-size: 0.65rem;
  padding: 6px 10px;
  text-align: left;
  border-bottom: 1px solid #e5e7eb;
}

.ram-result-table td {
  padding: 6px 10px;
  border-bottom: 1px solid #f3f4f6;
  color: #374151;
}

.ram-td-label {
  font-size: 0.72rem;
  color: #6b7280;
  font-family: monospace;
}

.ram-td-num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.ram-td-positive { color: #16a34a; font-weight: 600; }
.ram-td-negative { color: #dc2626; font-weight: 600; }

/* Variável objetivo — antes x depois */
.ram-obj-summary {
  border: 1px solid #fed7aa;
  background-color: #fff7ed;
  border-radius: 6px;
  padding: 10px 14px;
  margin-bottom: 14px;
}

.ram-obj-summary-head {
  display: flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #c2410c;
}

.ram-obj-summary-tag {
  margin-left: 8px;
  font-family: 'JetBrains Mono', 'SF Mono', Consolas, monospace;
  font-size: 0.7rem;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  color: #9a3412;
}

.ram-obj-summary-body {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
}

.ram-obj-summary-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ram-obj-summary-cell label {
  font-size: 0.6rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #9ca3af;
  margin: 0;
}

.ram-obj-summary-val {
  font-size: 1.05rem;
  font-weight: 700;
  color: #374151;
  font-variant-numeric: tabular-nums;
}

.ram-obj-summary-val--after { color: #c2410c; }

.ram-obj-summary-arrow { color: #9ca3af; }

.ram-obj-summary-var {
  margin-left: auto;
  font-size: 0.9rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

/* Loading / empty */
.ram-loading-config {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8rem;
  color: #9ca3af;
  padding: 12px 0;
}

.ram-empty {
  font-size: 0.8rem;
  color: #9ca3af;
  text-align: center;
  padding: 24px 0;
}

/* Footer */
.ram-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 4px;
  margin-top: auto;
}

.orange-btn {
  background-color: #ff5f00;
  border-color: #ff5f00;
  color: #ffffff;
}

.orange-btn:hover:not(:disabled) {
  background-color: #e04e00;
  border-color: #e04e00;
}

.orange-btn:disabled {
  background-color: #d1d5db;
  border-color: #d1d5db;
  cursor: not-allowed;
}
</style>

<!-- Modal de confirmação de cancelamento renderiza no body, fora do scope - sem scoped -->
<style>
.ram-cancel-body {
  padding: 20px 22px !important;
}

.ram-cancel-msg {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ram-cancel-msg p {
  margin: 0;
  line-height: 1.5;
  font-size: 0.88rem;
  color: #374151;
}

.ram-cancel-msg__intro {
  font-weight: 500;
}

.ram-cancel-msg__warn {
  padding: 10px 12px;
  background-color: #fef3c7;
  border-left: 3px solid #f59e0b;
  border-radius: 4px;
  font-size: 0.82rem !important;
  color: #92400e !important;
}

.ram-cancel-msg__hint {
  font-weight: 600;
  color: #1f2937 !important;
}
</style>
